<template>
  <div>
    <component
      v-bind="$attrs"
      :is="dropdownListView ? 'q-item' : 'div'"
      class="w-700 q-mx-auto"
      :class="{
        'q-px-sm q-pb-none': dropdownListView,
        'q-px-md': !!scrollAreaHeight || $q.screen.lt.sm
      }"
      data-test-id="site-location-lookup"
    >
      <template
        v-if="geoapifyEnabled"
      >
        <q-input
          v-if="selectedPlace"
          :model-value="selectedPlace"
          clearable
          autogrow
          @clear="selectedPlace = null"
          @update:model-value="selectedPlace = null"
        />
        <q-select
          v-else
          v-model="selectedPlace"
          :options="places"
          :option-label="(option) => option.properties ? option.properties.formatted : option"
          :class="{ 'q-mx-sm': !dropdownListView }"
          :use-input="!selectedPlace"
          clearable
          color="responsive"
          :placeholder="selectedPlace ? '' : $t('form.enterAddressForAutocompletePrompt')"
          :outlined="fieldType !== TEXT_FIELD_TYPES.UNDERLINE"
          :rounded="fieldType === TEXT_FIELD_TYPES.ROUNDED"
          :square="fieldType === TEXT_FIELD_TYPES.SQUARE"
          :bg-color="fieldType == TEXT_FIELD_TYPES.UNDERLINE || fieldOutlined ? undefined : 'responsive' "
          input-debounce="800"
          hide-dropdown-icon
          :dense="dense"
          @filter="filterPlaces"
        >
          <template #prepend>
            <q-icon name="fas fa-magnifying-glass" />
          </template>

          <template
            v-if="compactView || loadingPlaces"
            #append
          >
            <use-current-location-button
              class="self-center"
              padding="sm"
              :outline="buttonType === BUTTON_TYPES.OUTLINE"
              :class="getClasses(buttonType, buttonColor, buttonColorHex)"
              :style="getStyles(buttonType, buttonColor, buttonColorHex, buttonBorderRadius)"
              :push="buttonType === BUTTON_TYPES.STANDARD"
              icon-only
              :loading="loadingPlaces"
              :disable="loadingPlaces"
              @current-location-request="handleCurrentLocationRequest"
              @current-location-response="handleCurrentLocationResult"
            />
          </template>
        </q-select>
      </template>
      <template
        v-else
      >
        <q-input
          v-model="locationSearch"
          class="col q-mx-sm q-mb-sm"
          color="responsive"
          :outlined="fieldType !== TEXT_FIELD_TYPES.UNDERLINE"
          :rounded="fieldType === TEXT_FIELD_TYPES.ROUNDED"
          :square="fieldType === TEXT_FIELD_TYPES.SQUARE"
          :bg-color="fieldType == TEXT_FIELD_TYPES.UNDERLINE || fieldOutlined ? undefined : 'responsive' "
          clearable
          :dense="dense"
          :autofocus="dropdownListView"
          :label="$t('search.for.location')"
        >
          <template #prepend>
            <q-icon name="fas fa-magnifying-glass" />
          </template>
        </q-input>
      </template>
    </component>
    <slot name="value" />
    <component
      :is="dropdownListView ? 'q-item' : 'div'"
      v-if="!compactView && geoapifyEnabled"
      class="row w-700 q-mx-auto"
      :class="{
        'q-px-none q-pt-none': dropdownListView,
        'q-mb-md': !dropdownListView,
        'q-px-md': !!scrollAreaHeight
      }"
    >
      <use-current-location-button
        class="self-start q-ma-sm"
        padding="xs sm"
        :size="dropdownListView ? 'sm' : 'md'"
        :outline="buttonType === BUTTON_TYPES.OUTLINE"
        :class="{
          [getClasses(buttonType, buttonColor, buttonColorHex)]: true,
          'full-width': dropdownListView
        }"
        :style="getStyles(buttonType, buttonColor, buttonColorHex, buttonBorderRadius)"
        :push="buttonType === BUTTON_TYPES.STANDARD"
        :flat="buttonType === BUTTON_TYPES.FLAT"
        :loading="loadingPlaces"
        :disable="loadingPlaces"
        @current-location-request="handleCurrentLocationRequest"
        @current-location-response="handleCurrentLocationResult"
      />
    </component>
    <component
      :is="dropdownListView ? 'q-item' : 'div'"
      v-if="!$_.isEmpty(closestLocations)"
      :class="{
        'no-padding full-width': dropdownListView,
        'q-px-md': !!scrollAreaHeight || $q.screen.lt.sm,
        'q-mx-auto w-1000': !showSiteImage
      }"
    >
      <site-selector-locations-list
        :class="{ 'no-padding full-width no-border': dropdownListView }"
        :dense="dropdownListView"
        :sites-without-location-zone-id="currentClosestLocations"
        :site-locations="currentClosestLocations"
        :show-site-description="showSiteDescription"
        :card-color="cardColor"
        :card-color-hex="cardColorHex"
        :card-border-radius="cardBorderRadius"
        :redirect-home-page="redirectHomePage"
        :is-widget="isWidget"
        :dropdown-list-view="dropdownListView"
        :hide-address="hideAddress"
        show-distance
        :navigate-on-select="navigateOnSelect"
        :selected-locations="selectedLocations"
        @select-location="(site, action) => $emit('select-location', site, action)"
      >
        <template #title>
          <span
            :class="dropdownListView ? 'row text-body1 text-responsive-7 text-center q-px-md q-pt-sm' : 'text-h5'"
          >
            {{ $tc('siteLocation.closestLocation', 2) }}
          </span>
        </template>
        <template #action>
          <div :class="{ 'row justify-between': dropdownListView }">
            <q-btn
              class="col q-ma-sm"
              color="primary"
              :label="allItemsShown ? $t('siteLocation.allLocationsShown') : $t('actions.loadMore') "
              :disable="allItemsShown"
              @click="showMore"
            />
            <q-btn
              class="col q-ma-sm"
              color="secondary"
              :label="$t('nav.actions.showAll')"
              :disable="allItemsShown"
              @click="clearPlaceSelect"
            />
          </div>
        </template>
      </site-selector-locations-list>
    </component>
    <component

      :is="!!scrollAreaHeight ? 'q-scroll-area' : (dropdownListView ? 'q-item' : 'div')"
      v-if="(hasLocationSearchFilter || defaultShowAllLocations) && $_.isEmpty(closestLocations)"
      :style="{ 'height': scrollAreaHeight }"
      :class="{
        'q-px-md': !!scrollAreaHeight || $q.screen.lt.sm,
        'q-pa-none': dropdownListView
      }"
    >
      <div
        v-if="$_.isEmpty(filteredSiteLocations)"
        class="text-subtitle1 text-left q-py-sm q-px-md q-mx-auto w-700"
      >
        {{ $t('search.noResults') }}
      </div>
      <site-selector-locations-list
        :class="{ 'q-mx-auto w-1000': !showSiteImage }"
        :sites-without-location-zone-id="filteredSiteLocationsWithLocationZones"
        :location-zones="filteredLocationZones"
        :site-locations="filteredSiteLocations"
        :all-sites-have-images="allSitesHaveImages"
        :show-site-image="showSiteImage"
        :show-site-description="showSiteDescription"
        :card-color="cardColor"
        :card-color-hex="cardColorHex"
        :card-border-radius="cardBorderRadius"
        :redirect-home-page="redirectHomePage"
        :is-widget="isWidget"
        :dropdown-list-view="dropdownListView"
        :hide-address="hideAddress"
        :compact-view="compactView"
        :navigate-on-select="navigateOnSelect"
        :default-opened="defaultOpened || hasLocationSearchFilter || locationZones?.length === 1"
        :selected-locations="selectedLocations"
        :flat="flat"
        @select-location="(site, action) => $emit('select-location', site, action)"
      />
    </component>
  </div>
</template>

<script>
import { Notify } from 'quasar'
import { geoapifyEnabled, region } from 'src/config'
import { BUTTON_TYPES, SELECTOR_DISPLAY_TYPES, TEXT_FIELD_TYPES } from 'src/models/page/Section'
import SiteLocationsListMixin from 'src/modules/consumer/components/mixins/SiteLocationsListMixin'
import SiteSelectorLocationsList from 'src/modules/consumer/components/site-selector/SiteSelectorLocationsList'
import UseCurrentLocationButton from 'src/modules/consumer/components/site-selector/UseCurrentLocationButton'
export default {
  name: 'SiteLocationLookUp',
  components: {
    UseCurrentLocationButton,
    SiteSelectorLocationsList
  },
  mixins: [SiteLocationsListMixin],
  props: {
    isMobileView: {
      type: Boolean,
      required: false,
      default: false
    },
    cardColor: {
      type: String,
      required: false,
      default: 'primary'
    },
    cardBorderRadius: {
      type: Number,
      required: false,
      default: 4
    },
    cardColorHex: {
      type: String,
      required: false,
      default: '#ffffff'
    },
    compactView: {
      type: Boolean,
      required: false,
      default: false
    },
    fieldType: {
      type: String,
      required: false,
      default: 'filled'
    },
    fieldOutlined: {
      type: Boolean,
      required: false,
      default: false
    },
    showSiteImage: {
      type: Boolean,
      required: false,
      default: false
    },
    showSiteDescription: {
      type: Boolean,
      required: false,
      default: false
    },
    defaultOpened: {
      type: Boolean,
      required: false,
      default: false
    },
    redirectHomePage: {
      type: Boolean,
      required: false,
      default: false
    },
    buttonType: {
      type: String,
      required: false,
      default: BUTTON_TYPES.OUTLINE
    },
    buttonColor: {
      type: String,
      required: false,
      default: 'responsive'
    },
    buttonColorHex: {
      type: String,
      required: false,
      default: '#FFFFFF'
    },
    buttonBorderRadius: {
      type: Number,
      required: false,
      default: 4
    },
    isWidget: {
      type: Boolean,
      required: false,
      default: false
    },
    dropdownListView: {
      type: Boolean,
      required: false,
      default: false
    },
    hideAddress: {
      type: Boolean,
      required: false,
      default: false
    },
    checkboxView: {
      type: Boolean,
      required: false,
      default: false
    },
    navigateOnSelect: {
      type: Boolean,
      required: false,
      default: true
    },
    dense: {
      type: Boolean,
      required: false,
      default: false
    },
    flat: {
      type: Boolean,
      required: false,
      default: false
    },
    defaultShowAllLocations: {
      type: Boolean,
      required: false,
      default: false
    },
    scrollAreaHeight: {
      type: String,
      required: false,
      default: null
    },
    selectedLocations: {
      type: Array,
      required: false,
      default: () => []
    },
    flattenZoneGroups: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: ['all-locations-shown', 'loading', 'select-location'],
  data () {
    return {
      mapsService: null,
      closestLocations: null,
      closestLocationBatchSize: 3,
      closestLocationQuantity: 3,
      searchboxFocus: false,
      locationSearch: '',
      places: [],
      selectedPlace: null,
      loadingPlaces: false,
      addressSearchString: ''
    }
  },
  computed: {
    hasLocationSearchFilter () {
      return !!this.locationSearch || !!this.selectedPlace
    },
    filteredSiteLocations () {
      if (!this.hasLocationSearchFilter) return this.siteLocations
      return _.filter(this.siteLocations, site => _.includes(_.toLower(site.name), _.toLower(this.locationSearch)))
    },
    filteredSiteLocationsWithLocationZones () {
      if (!this.hasLocationSearchFilter) return this.sitesWithoutLocationZoneId
      return _.filter(this.sitesWithoutLocationZoneId, site => _.includes(_.toLower(site.name), _.toLower(this.locationSearch)))
    },
    filteredLocationZones () {
      if (!this.hasLocationSearchFilter) return this.locationZones

      return _.map(this.locationZones, zone => {
        return {
          ...zone,
          visibleSites: _.filter(zone.visibleSites, site => _.includes(_.toLower(site.name), _.toLower(this.locationSearch)))
        }
      })
      // filter out zones with no visible sites
        .filter(zone => zone.visibleSites.length > 0)
    },
    currentClosestLocations () {
      return this.closestLocations?.slice(0, this.closestLocationQuantity) || []
    },
    countries () {
      // some countries have multiple iso31661 codes separated by commas
      const countries = _(this.sites.data)
        .map(site => _.map(site.iso31661.split(','), countryCode => countryCode.toLowerCase().trim()))
        .flatten()
        .uniq()
        .value()
      // google maps autocomplete only supports up to 5 countries
      return countries.length > 5 ? [] : countries
    },
    allItemsShown () {
      return this.closestLocationQuantity >= this.closestLocations.length
    }

  },
  watch: {
    hasLocationSearchFilter (value) {
      if (value === true) {
        this.$emit('all-locations-shown', false)
      } else {
        this.$emit('all-locations-shown', true)
      }
    },
    async selectedPlace (value) {
      if (!value) {
        this.clearPlaceSelect()
      } else {
        this.placeSelect(value)
      }
    },
    async addressSearchString (value) {
      this.places = await this.getPlacesFromString(value)
    }
  },
  created () {
    _.extend(this, {
      region,
      geoapifyEnabled,
      TEXT_FIELD_TYPES,
      SELECTOR_DISPLAY_TYPES,
      BUTTON_TYPES
    })
  },
  methods: {
    clearPlaceSelect () {
      this.closestLocationQuantity = 0
      this.closestLocations = null
      this.$emit('all-locations-shown', true)
    },
    async filterPlaces (value, update) {
      update(() => {
        this.addressSearchString = value
      })
    },
    async getPlacesFromString (str) {
      this.loadingPlaces = true
      const places = await this.$geoapify?.getAutocompleteOptions(str, this.countries) || []
      this.loadingPlaces = false
      return places
    },

    showMore () {
      this.closestLocationQuantity += this.closestLocationBatchSize
    },
    async placeSelect (placeData) {
      this.$emit('loading', true)
      this.loadingPlaces = true
      this.closestLocations = null
      this.closestLocationQuantity = this.closestLocationBatchSize
      try {
        this.closestLocations = await this.$geoapify.getClosestLocations(this.$geoapify.getCoordinatesFromAutocomplete(placeData), this.siteLocations)
        this.$emit('all-locations-shown', false)
      } catch (error) {
        this.$mh.showErrors(error)
      }
      this.$emit('loading', false)
      this.loadingPlaces = false
    },
    handleCurrentLocationRequest () {
      this.$emit('loading', true)
      this.loadingPlaces = true
    },
    async handleCurrentLocationResult (origin) {
      try {
        if (!_.isNil(origin)) {
          this.closestLocations = await this.$geoapify?.getClosestLocations(origin, this.siteLocations) || []

          // Check if we got valid results
          if (_.isNil(this.closestLocations) || _.isEmpty(this.closestLocations)) {
            Notify.create({
              message: this.$t('siteLocation.failedToGetClosestLocations'),
              type: 'negative',
              position: 'top'
            })
            this.clearPlaceSelect()
          }
        } else {
          Notify.create({
            message: this.$t('siteLocation.failedToGetCurrentLocation'),
            type: 'negative',
            position: 'top'
          })
          this.clearPlaceSelect()
        }
      } catch (error) {
        Notify.create({
          message: this.$t('siteLocation.failedToGetClosestLocations'),
          type: 'negative',
          position: 'top'
        })
        this.clearPlaceSelect()
      }
      this.$emit('loading', false)
      this.loadingPlaces = false
    }
  }
}
</script>

<style lang="scss" scoped>
:deep(.address-display) {
  padding-top: 24px;
  padding-bottom: 8px;
}

:deep(.address-autocomplete) {
  border: none;
  width: 100%
}

:deep(.address-autocomplete:focus) {
  outline: none;
}

:deep(.autocomplete-wrapper) {
  .q-field__native {
    display: none;
  }
}

:deep(.pac-container, .pac-item) {
    /* GOOGLE MAPS AUTOCOMPLETE DROPDOWN IN ADMIN DOESN'T DISPLAY CORRECTLY WITHOUT THIS */
    z-index: 10000 !important;
 }

</style>
